import { motion } from "motion/react";
import React, { useMemo } from "react";
import { useLongPress } from "../../../hooks/useLongPress.hook";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import ArrowLeftToRight from "../../../image/icons/map/details/ArrowLeftToRight.svg?react";
import Clock from "../../../image/icons/map/details/ClockIcon.svg?react";
import Dot from "../../../image/icons/map/details/Dot.svg?react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { isDisplayableEvaluatedPrice, resolveItineraryPriceForDisplay } from "../../../tools/ItineraryPricingEvaluationDisplay.tools";
import { formatTime } from "../../../tools/TimeRelated";
import { getTransportModeGroup, TransportModeGroup } from "../../../tools/TransportModeGroupsTools";
import { ItineraryType, type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import ItineraryRealtimeClock, { resolveSummaryArrivalRealtimeSource } from "../shared/ItineraryRealtimeClock";
import {
    isUiMotionEnabled,
    resolveUiSpring,
} from "../../shared/motion/uiMotion.tools";
import ItineraryPriceBadge from "../pricing/ItineraryPriceBadge";
import {
    buildItineraryPricePillChromeLoadingStyle,
    ITINERARY_PRICE_PILL_CHROME_BASE_CLASSNAME,
    ITINERARY_PRICE_PILL_CHROME_FILLED_CLASSNAME,
} from "../pricing/itineraryPricePill.styles";
import ItineraryItemSavedHeader from "../savedItinerary/ItineraryItemSavedHeader";
import {
    buildItineraryListCardStyle,
    ITINERARY_LIST_CARD_CHROME_CLASSNAME,
} from "./itineraryListCard.styles";
import TravelProgress from "./TravelProgress";

interface ItineraryItemSavedListHeader {
    "originLabel":      string;
    "destinationLabel": string;
    "displayDate":      string;
    "onRemove"?:        () => void;

    /** Overrides the header DOM id root (defaults to `itinerary.id`); unique per list row. */
    "itineraryDomId"?: string;

    /** Shows a punched-ticket stamp marking the entry as a purchase. */
    "isPurchased"?: boolean;
}

interface ItineraryItemProps {

    /** Whether TravelProgress segments should grow in after search completes. */
    "animateTravelProgress"?: boolean;

    "handleItineraryClick":  ( itinerary: ItineraryDTO ) => void;
    "onItineraryLongPress"?: ( itinerary: ItineraryDTO ) => void;
    "index":                 number;
    "isDarkUi":              boolean;
    "isEuro":                boolean;
    "isPriceLoading":        boolean;
    "itinerary":             ItineraryDTO;
    "pricingPreferences":    ItineraryPricingPreferencesDTO;

    /** When `false`, hides the list price badge (saved-itinerary list). */
    "showPrice"?: boolean;

    /** Optional header shown only for saved-itinerary list items. */
    "savedListHeader"?: ItineraryItemSavedListHeader;
}

/**
 * Renders the itinerary price badge, including a fade-in when pricing completes.
 *
 * @param props Price badge props.
 * @returns Price badge node.
 */
const ItineraryItemPriceBadge: React.FC<{
    "isDarkUi":               boolean;
    "isEuro":                 boolean;
    "itinerary":              ItineraryDTO;
    "pricingPreferences":     ItineraryPricingPreferencesDTO;
    "shouldShowNumericPrice": boolean;
    "shouldShowPriceBadge":   boolean;
}> = ( {
    isDarkUi,
    isEuro,
    itinerary,
    pricingPreferences,
    shouldShowNumericPrice,
    shouldShowPriceBadge,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const palette = getUIColorPalette( isDarkUi );
    const offerLoadingAccent = palette.itineraryDetailsOfferLoadingAccent;
    const pricePillClassName = shouldShowNumericPrice
        ? `ml-auto ${ ITINERARY_PRICE_PILL_CHROME_FILLED_CLASSNAME }`
        : `ml-auto ${ ITINERARY_PRICE_PILL_CHROME_BASE_CLASSNAME }`;
    const pricePillStyle = shouldShowNumericPrice
        ? undefined
        : buildItineraryPricePillChromeLoadingStyle( offerLoadingAccent );

    if ( !shouldShowPriceBadge ) {
        return null;
    }

    const badgeContent = shouldShowNumericPrice
        ? (
            <ItineraryPriceBadge
                className="text-inherit"
                isEuro={isEuro}
                itinerary={itinerary}
                pricingPreferences={pricingPreferences}
            />
        )
        : (
            <span className="inline-flex items-center gap-1">
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {" "}
                CHF
            </span>
        );

    if ( !animationsEnabled ) {
        return (
            <span
                className={pricePillClassName}
                style={pricePillStyle}
            >
                {badgeContent}
            </span>
        );
    }

    return (
        <motion.span
            animate={{
                "opacity": 1,
                "scale":   1,
                "y":       0,
            }}
            className={pricePillClassName}
            initial={shouldShowNumericPrice
                ? {
                    "opacity": 0,
                    "scale":   0.94,
                    "y":       -4,
                }
                : {
                    "opacity": 1,
                    "scale":   1,
                    "y":       0,
                }}
            key={shouldShowNumericPrice ? `priced-${ itinerary.id ?? itinerary.startTime }` : `pricing-${ itinerary.id ?? itinerary.startTime }`}
            style={pricePillStyle}
            transition={resolveUiSpring( reducedMotion, "gentle" )}
        >
            {badgeContent}
        </motion.span>
    );
};

const ItineraryItem: React.FC<ItineraryItemProps> = ( {
    animateTravelProgress = false,
    handleItineraryClick,
    onItineraryLongPress,
    index,
    isDarkUi,
    isEuro,
    isPriceLoading,
    itinerary,
    pricingPreferences,
    showPrice = true,
    savedListHeader,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const itineraryCardStyle = useMemo(
        (): React.CSSProperties => buildItineraryListCardStyle( palette, isDarkUi ),
        [ isDarkUi, palette ],
    );
    const formattedDuration: string = formatTime( itinerary.duration ?? 0 );
    const isPendingCompletion = itinerary.finalized === false;
    const longPressEnabled = __IS_DEV__ && onItineraryLongPress !== undefined && !isPendingCompletion;
    const { "bind": longPressBind } = useLongPress( {
        "enabled": longPressEnabled,
        "onClick": () => {
            handleItineraryClick( itinerary );
        },
        "onLongPress": () => {
            onItineraryLongPress?.( itinerary );
        },
    } );
    const displayListPrice = resolveItineraryPriceForDisplay( itinerary, pricingPreferences );
    const hasPrice = isDisplayableEvaluatedPrice( displayListPrice );
    const isWalkOnlyItinerary = itinerary.legs.length > 0 && itinerary.legs.every( ( leg ) => getTransportModeGroup( leg.mode ) === TransportModeGroup.WALK );
    const arrivalRealtime = resolveSummaryArrivalRealtimeSource( itinerary );

    // showPrice gates saved-list rows; !isPriceLoading swaps spinner → amount once evaluation settles.
    const shouldShowNumericPrice = showPrice && !isPriceLoading && hasPrice && ( !isWalkOnlyItinerary || itinerary.type === ItineraryType.CAR );
    const shouldShowPriceBadge = showPrice && ( shouldShowNumericPrice || isPriceLoading );

    const itineraryDomId = `itinerary-list-item-${ itinerary.id ?? String( index ) }`;

    return (
        <div
            className={`${ ITINERARY_LIST_CARD_CHROME_CLASSNAME } ${ isPendingCompletion ? "cursor-not-allowed opacity-70" : "cursor-pointer" }`}
            id={itineraryDomId}
            key={index}
            {...( longPressEnabled ? longPressBind : {} )}
            onClick={longPressEnabled
                ? undefined
                : () => {
                    if ( !isPendingCompletion ) {
                        handleItineraryClick( itinerary );
                    }
                }}
            style={itineraryCardStyle}
        >
            {savedListHeader !== undefined
                ? (
                    <ItineraryItemSavedHeader
                        destinationLabel={savedListHeader.destinationLabel}
                        displayDate={savedListHeader.displayDate}
                        isDarkUi={isDarkUi}
                        isPurchased={savedListHeader.isPurchased}
                        itineraryId={savedListHeader.itineraryDomId ?? itinerary.id ?? String( index )}
                        onRemove={savedListHeader.onRemove}
                        originLabel={savedListHeader.originLabel}
                    />
                )
                : null}
            <div
                className="flex items-center"
                id={`${ itineraryDomId }-header`}
            >
                <div className="flex justify-center items-center">
                    <Clock
                        aria-label="Petite horloge"
                        className="inline-block align-middle shrink-0 w-[16px] h-[16px]"
                        style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                    />
                    <h4
                        className="pl-2 text-xs flex items-center"
                        style={{ "color": "var(--itinerary-text-primary, #0f172a)" }}
                    >
                        {formattedDuration}
                        <Dot
                            className="ml-1 mr-1 inline-block align-middle self-center"
                            style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                        />
                        <b>
                            {new Date( itinerary.startTime ).toLocaleTimeString( [], {
                                "hour":   "2-digit",
                                "minute": "2-digit",
                                "hour12": false,
                            } )}
                        </b>
                        <ArrowLeftToRight
                            className="ml-1 mr-1 w-3 inline-block align-middle self-center"
                            style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                        />
                        <ItineraryRealtimeClock
                            className="inline-block align-middle text-xs font-semibold"
                            colorScheduledTimeIso={arrivalRealtime.colorScheduledTimeIso}
                            colorTimeIso={arrivalRealtime.colorTimeIso}
                            hasRealtimeInfo={arrivalRealtime.hasRealtimeInfo}
                            isDarkUi={isDarkUi}
                            timeIso={itinerary.endTime}
                        />
                    </h4>
                </div>
                <ItineraryItemPriceBadge
                    isDarkUi={isDarkUi}
                    isEuro={isEuro}
                    itinerary={itinerary}
                    pricingPreferences={pricingPreferences}
                    shouldShowNumericPrice={shouldShowNumericPrice}
                    shouldShowPriceBadge={shouldShowPriceBadge}
                />
            </div>
            <div
                className="relative z-0 mt-1.5 min-w-0"
                id={`${ itineraryDomId }-travel-progress`}
            >
                <TravelProgress
                    animateSegmentReveal={animateTravelProgress}
                    isCarpoolIncomplete={isPendingCompletion}
                    itineraryRowIndex={index}
                    legs={itinerary.legs}
                    pricingSegments={itinerary.pricingSegments}
                />
            </div>
        </div>
    );
};

export default ItineraryItem;
